import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Search, ChevronDown, TrendingUp, TrendingDown, Minus, Award, AlertTriangle,
  Target, Shield, Calendar, DollarSign, Users, Sparkles, MessageSquare, Building,
  ArrowRight, CheckCircle2, XCircle, Lightbulb, AlertOctagon, Clock, Download, FileText,
} from 'lucide-react';
import {
  ResponsiveContainer, Tooltip,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend,
} from 'recharts';
import { formatDateAU } from '../../utils/dateFormat';
import { exportAsHTML, exportAsPDF } from '../../utils/exportDeepDive';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => v == null ? '—' : `$${Math.round(v).toLocaleString()}`;
const fmtPct = (v, digits = 1) => v == null ? '—' : `${v.toFixed(digits)}%`;
const fmtNum = (v, digits = 1) => v == null ? '—' : v.toFixed(digits);

// ── Helpers ─────────────────────────────────────────────────────────────────
const compareDelta = (target, peer, higherIsBetter = true) => {
  if (peer == null || target == null) return { value: null, pct: null, dir: 'neutral' };
  const diff = target - peer;
  const pct = peer !== 0 ? (diff / Math.abs(peer)) * 100 : null;
  const isPositive = higherIsBetter ? diff > 0 : diff < 0;
  const dir = Math.abs(diff) < 0.001 ? 'neutral' : isPositive ? 'up' : 'down';
  return { value: diff, pct, dir };
};

const DeltaPill = ({ delta, suffix = '%' }) => {
  if (!delta || delta.pct == null) return <span className="text-[10px] text-[#A3A3A3]">—</span>;
  const colorClasses = {
    up: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    down: 'bg-red-50 text-red-700 border-red-200',
    neutral: 'bg-[#F5F5F5] text-[#737373] border-[#E5E5E5]',
  };
  const Icon = delta.dir === 'up' ? TrendingUp : delta.dir === 'down' ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${colorClasses[delta.dir]}`}>
      <Icon className="w-2.5 h-2.5" strokeWidth={2} />
      {delta.pct > 0 ? '+' : ''}{delta.pct.toFixed(1)}{suffix}
    </span>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────
const KPICard = ({ icon: Icon, label, value, sublabel, accent = 'gray', target, peer, higherIsBetter = true, testId }) => {
  const accents = {
    gray: 'border-[#E5E5E5]', emerald: 'border-emerald-200 bg-emerald-50/30',
    amber: 'border-amber-200 bg-amber-50/30', red: 'border-red-200 bg-red-50/30',
    blue: 'border-blue-200 bg-blue-50/30',
  };
  const delta = target != null && peer != null ? compareDelta(target, peer, higherIsBetter) : null;
  return (
    <div data-testid={testId} className={`bg-white border rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)] ${accents[accent]}`}>
      <div className="flex items-start justify-between mb-2">
        <Icon className="w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
        {delta && <DeltaPill delta={delta} />}
      </div>
      <p className="text-[10px] font-semibold text-[#A3A3A3] uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-[#171717] mt-1 font-heading">{value}</p>
      {sublabel && <p className="text-[10px] text-[#737373] mt-0.5">{sublabel}</p>}
    </div>
  );
};

const RankBadge = ({ rank, total, label }) => {
  if (!rank) return null;
  const pct = (rank / total) * 100;
  let bg, txt;
  if (pct <= 25) { bg = 'bg-emerald-50 border-emerald-200'; txt = 'text-emerald-700'; }
  else if (pct <= 75) { bg = 'bg-amber-50 border-amber-200'; txt = 'text-amber-700'; }
  else { bg = 'bg-red-50 border-red-200'; txt = 'text-red-700'; }
  return (
    <div className={`px-3 py-1.5 rounded-[8px] border ${bg}`}>
      <p className="text-[9px] font-semibold uppercase tracking-wider text-[#A3A3A3]">{label} Rank</p>
      <p className={`text-sm font-bold ${txt}`}>#{rank} <span className="text-xs font-normal opacity-60">/ {total}</span></p>
    </div>
  );
};

const SWOTQuadrant = ({ title, items, accent, icon: Icon, testId }) => {
  const config = {
    s: { ring: 'border-emerald-200 bg-emerald-50/40', header: 'text-emerald-700', dot: 'bg-emerald-500' },
    w: { ring: 'border-red-200 bg-red-50/40', header: 'text-red-700', dot: 'bg-red-500' },
    o: { ring: 'border-blue-200 bg-blue-50/40', header: 'text-blue-700', dot: 'bg-blue-500' },
    t: { ring: 'border-amber-200 bg-amber-50/40', header: 'text-amber-700', dot: 'bg-amber-500' },
  };
  const c = config[accent];
  return (
    <div data-testid={testId} className={`border rounded-[12px] p-5 ${c.ring}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${c.header}`} strokeWidth={1.5} />
        <h3 className={`text-xs font-bold uppercase tracking-wider ${c.header}`}>{title}</h3>
        <span className="ml-auto text-[10px] font-semibold text-[#A3A3A3]">{items.length}</span>
      </div>
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item.title} className="flex gap-2.5">
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${c.dot}`} />
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-xs font-semibold text-[#171717]">{item.title}</p>
                {item.metric && item.metric !== '—' && (
                  <span className="text-[10px] font-mono font-semibold text-[#525252] flex-shrink-0">{item.metric}</span>
                )}
              </div>
              <p className="text-[11px] text-[#525252] leading-relaxed mt-0.5">{item.detail}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const BenchmarkBar = ({ label, target, peer, portfolio, formatter = fmt, higherIsBetter = true }) => {
  const max = Math.max(target || 0, peer || 0, portfolio || 0, 1);
  const tw = ((target || 0) / max) * 100;
  const pw = ((peer || 0) / max) * 100;
  const pow = ((portfolio || 0) / max) * 100;
  const delta = compareDelta(target, peer, higherIsBetter);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#525252]">{label}</p>
        <DeltaPill delta={delta} />
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-16 text-[10px] text-[#171717] font-semibold">This Group</span>
          <div className="flex-1 h-5 rounded-[3px] bg-[#F5F5F5] overflow-hidden relative">
            <div className="h-full bg-[#171717]" style={{ width: `${tw}%` }} />
          </div>
          <span className="w-24 text-right text-[10px] font-mono font-semibold text-[#171717]">{formatter(target)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-16 text-[10px] text-[#737373]">Peer avg</span>
          <div className="flex-1 h-3 rounded-[3px] bg-[#F5F5F5] overflow-hidden">
            <div className="h-full bg-[#A3A3A3]" style={{ width: `${pw}%` }} />
          </div>
          <span className="w-24 text-right text-[10px] font-mono text-[#737373]">{formatter(peer)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-16 text-[10px] text-[#A3A3A3]">Portfolio</span>
          <div className="flex-1 h-3 rounded-[3px] bg-[#F5F5F5] overflow-hidden">
            <div className="h-full bg-[#D4D4D4]" style={{ width: `${pow}%` }} />
          </div>
          <span className="w-24 text-right text-[10px] font-mono text-[#A3A3A3]">{formatter(portfolio)}</span>
        </div>
      </div>
    </div>
  );
};

const GroupSelector = ({ groups, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query) return groups.slice(0, 50);
    const q = query.toLowerCase();
    return groups.filter(g =>
      g.basePlan.toLowerCase().includes(q) ||
      (g.propertyName || '').toLowerCase().includes(q) ||
      (g.primaryManager || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [groups, query]);

  const selected = groups.find(g => g.basePlan === value);

  return (
    <div className="relative">
      <button
        data-testid="group-selector-btn"
        onClick={() => setOpen(!open)}
        className="w-full md:w-[480px] flex items-center gap-3 px-4 py-3 bg-white border border-[#E5E5E5] rounded-[10px] text-left hover:border-[#171717] transition-colors"
      >
        <Building className="w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          {selected ? (
            <>
              <p className="text-sm font-semibold text-[#171717] truncate">{selected.propertyName || selected.basePlan}</p>
              <p className="text-[10px] text-[#737373] font-mono">{selected.basePlan} · {selected.primaryManager} · {fmt(selected.totalRevenue)}</p>
            </>
          ) : (
            <p className="text-sm text-[#A3A3A3]">Select a plan group…</p>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-[#A3A3A3] transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1.5 w-full md:w-[480px] bg-white border border-[#E5E5E5] rounded-[10px] shadow-xl z-20 max-h-[480px] overflow-hidden">
          <div className="px-3 py-2.5 border-b border-[#E5E5E5] sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A3]" strokeWidth={1.5} />
              <input
                data-testid="group-selector-search"
                type="text"
                placeholder="Search by property, plan, or manager…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoFocus
                className="pl-9 pr-4 py-2 w-full text-xs border border-[#E5E5E5] rounded-[8px] bg-[#FAFAFA] focus:outline-none focus:border-[#171717]"
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-[400px]">
            {filtered.map(g => (
              <button
                key={g.basePlan}
                data-testid={`group-option-${g.basePlan}`}
                onClick={() => { onChange(g.basePlan); setOpen(false); setQuery(''); }}
                className={`w-full px-4 py-2.5 text-left hover:bg-[#F8F9FA] border-b border-[#F5F5F5] last:border-0 ${value === g.basePlan ? 'bg-[#F8F9FA]' : ''}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-[#171717] truncate">{g.propertyName || g.basePlan}</p>
                    <p className="text-[10px] text-[#737373] font-mono">{g.basePlan} · {g.primaryManager}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] font-semibold text-[#171717] font-mono">{fmt(g.totalRevenue)}</p>
                    <p className="text-[10px] text-[#737373]">{fmtPct(g.margin, 0)} margin</p>
                  </div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-[#A3A3A3] py-6 text-center">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────
const PlanGroupDeepDive = ({ initialGroup, onGroupChange }) => {
  const [groupList, setGroupList] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(initialGroup || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plan-group-deep-dive/list`);
      setGroupList(res.data.groups || []);
      if (!selectedGroup && res.data.groups?.length > 0) {
        setSelectedGroup(res.data.groups[0].basePlan);
      }
    } catch (error) {
      console.error('Failed to fetch group list:', error);
    } finally {
      setListLoading(false);
    }
  }, [selectedGroup]);

  const fetchDeepDive = useCallback(async (group) => {
    if (!group) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/plan-group-deep-dive`, { params: { group } });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch deep dive:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchDeepDive(selectedGroup); }, [selectedGroup, fetchDeepDive]);

  // Sync external selection changes (e.g., from global search)
  useEffect(() => {
    if (initialGroup && initialGroup !== selectedGroup) {
      setSelectedGroup(initialGroup);
    }
  }, [initialGroup, selectedGroup]);

  const handleSelectChange = useCallback((bp) => {
    setSelectedGroup(bp);
    if (onGroupChange) onGroupChange(bp);
  }, [onGroupChange]);

  if (listLoading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="deep-dive-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#171717] font-heading">Plan Group Deep Dive</h1>
          <p className="text-sm text-[#737373] mt-1">SWOT, peer benchmarking, and renewal advisor for any client plan group.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <GroupSelector groups={groupList} value={selectedGroup} onChange={handleSelectChange} />
          {data && !loading && (
            <>
              <button
                data-testid="export-html-btn"
                onClick={() => exportAsHTML(data)}
                className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-[#171717] bg-white border border-[#E5E5E5] rounded-[8px] hover:border-[#171717] transition-colors"
                title="Download as standalone HTML report"
              >
                <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                HTML
              </button>
              <button
                data-testid="export-pdf-btn"
                onClick={() => exportAsPDF(data)}
                className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-white bg-[#171717] rounded-[8px] hover:bg-[#0A0A0A] transition-colors"
                title="Download as PDF report"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                PDF
              </button>
            </>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
        </div>
      )}

      {!loading && data && <DeepDiveContent data={data} />}
    </div>
  );
};

// ── Content Renderer ────────────────────────────────────────────────────────
const DeepDiveContent = ({ data }) => {
  const { target, peers, peerStats, portfolioStats, swot, renewal, npsComments, ranks, peerMode } = data;

  const actionColors = {
    emerald: 'bg-blue-50 border-blue-300 text-blue-800',
    amber: 'bg-blue-50 border-blue-300 text-blue-800',
    orange: 'bg-blue-50 border-blue-300 text-blue-800',
    red: 'bg-blue-50 border-blue-300 text-blue-800',
  };

  const radarData = [
    { metric: 'Revenue', target: scale(target.totalRevenue, peerStats.revenue), peer: 100 },
    { metric: 'Rev / Lot', target: scale(target.revenuePerLot, peerStats.revenuePerLot), peer: 100 },
    { metric: 'Margin', target: scale(target.margin, peerStats.margin), peer: 100 },
    { metric: 'NPS', target: scale(target.avgNPS, peerStats.avgNPS), peer: 100 },
    { metric: 'Compliance', target: scale(target.avgCompliance, peerStats.avgCompliance), peer: 100 },
    { metric: 'Lots', target: scale(target.totalOccupiableLots, peerStats.occupiableLots), peer: 100 },
    { metric: 'Gross Margin', target: scale(target.profit, peerStats.profit), peer: 100 },
  ];

  return (
    <>
      {/* Hero */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Building className="w-5 h-5 text-[#171717]" strokeWidth={1.5} />
              <h2 className="text-xl font-semibold text-[#171717] font-heading">{target.propertyName || target.basePlan}</h2>
              <span className="text-[10px] font-mono font-semibold text-[#525252] bg-[#F5F5F5] px-2 py-0.5 rounded">{target.basePlan}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-[#737373]">
              <span className="flex items-center gap-1.5"><Users className="w-3 h-3" strokeWidth={1.5} /> {target.primaryManager}</span>
              <span>·</span>
              <span>{target.planCount} plan{target.planCount > 1 ? 's' : ''}</span>
              <span>·</span>
              <span>{target.totalOccupiableLots} lots</span>
              <span>·</span>
              <span className="font-semibold text-[#525252]">{target.dominantPersona}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <RankBadge rank={ranks.revenue.rank} total={ranks.revenue.total} label="Revenue" />
            <RankBadge rank={ranks.margin.rank} total={ranks.margin.total} label="Margin" />
            {ranks.nps.rank && <RankBadge rank={ranks.nps.rank} total={ranks.nps.total} label="NPS" />}
          </div>
        </div>
      </div>

      {/* Renewal Advisor */}
      <div data-testid="renewal-advisor" className={`border-2 rounded-[12px] p-6 ${actionColors[renewal.actionColor]}`}>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:w-1/3">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">Advisor Recommendation</p>
            </div>
            <h3 className="text-2xl font-bold font-heading mb-1">{renewal.action}</h3>
            <p className="text-xs leading-relaxed opacity-90">{renewal.actionSummary}</p>
          </div>
          <div className="lg:w-1/3 space-y-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1">Renewal Priority</p>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                <p className="text-sm font-semibold">{renewal.priority}</p>
              </div>
              {renewal.expiryDate && (
                <p className="text-[11px] opacity-75 mt-0.5">
                  Expires {formatDateAU(renewal.expiryDate)}
                  {renewal.daysToExpiry != null && (
                    <span className="ml-1">({renewal.daysToExpiry > 0 ? `in ${renewal.daysToExpiry} days` : `${Math.abs(renewal.daysToExpiry)} days overdue`})</span>
                  )}
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1">Fee Guidance</p>
              <p className="text-xs leading-relaxed">{renewal.feeGuidance}</p>
            </div>
          </div>
          <div className="lg:w-1/3">
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-2">Talking Points</p>
            <ul className="space-y-1.5">
              {renewal.talkingPoints.map((tp) => (
                <li key={tp} className="flex items-start gap-2 text-xs leading-relaxed">
                  <ArrowRight className="w-3 h-3 flex-shrink-0 mt-0.5 opacity-60" strokeWidth={2} />
                  <span>{tp}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
        <KPICard
          testId="kpi-revenue" icon={DollarSign} label="Revenue" value={fmt(target.totalRevenue)}
          sublabel={`${target.totalOccupiableLots} lots`} accent="gray"
          target={target.totalRevenue} peer={peerStats.revenue}
        />
        <KPICard
          testId="kpi-revenue-per-lot" icon={Building} label="Revenue / Lot" value={fmt(target.revenuePerLot)}
          sublabel="fee intensity"
          accent={peerStats.revenuePerLot && target.revenuePerLot > peerStats.revenuePerLot * 1.15 ? 'emerald' : peerStats.revenuePerLot && target.revenuePerLot < peerStats.revenuePerLot * 0.85 ? 'amber' : 'gray'}
          target={target.revenuePerLot} peer={peerStats.revenuePerLot}
        />
        <KPICard
          testId="kpi-margin" icon={TrendingUp} label="Gross Margin %" value={fmtPct(target.margin)}
          sublabel={`${fmt(target.profit)} gross margin`}
          accent={target.margin >= 25 ? 'emerald' : target.margin >= 15 ? 'amber' : 'red'}
          target={target.margin} peer={peerStats.margin}
        />
        <KPICard
          testId="kpi-workload" icon={Target} label="Workload" value={fmt(target.totalWorkload)}
          sublabel={`${fmt(target.workloadPerLot)}/lot`}
          accent="gray" target={target.workloadPerLot} peer={peerStats.workloadPerLot} higherIsBetter={false}
        />
        <KPICard
          testId="kpi-nps" icon={Users} label="NPS" value={target.avgNPS != null ? fmtNum(target.avgNPS) : '—'}
          sublabel={target.npsResponseCount > 0 ? `${target.promoters}P / ${target.passives}Pa / ${target.detractors}D` : 'no responses'}
          accent={target.avgNPS == null ? 'gray' : target.avgNPS >= 9 ? 'emerald' : target.avgNPS >= 7 ? 'amber' : 'red'}
          target={target.avgNPS} peer={peerStats.avgNPS}
        />
        <KPICard
          testId="kpi-compliance" icon={Shield} label="Compliance" value={target.avgCompliance != null ? `${target.avgCompliance.toFixed(0)}%` : '—'}
          sublabel="quality score"
          accent={target.avgCompliance == null ? 'gray' : target.avgCompliance >= 90 ? 'emerald' : target.avgCompliance >= 70 ? 'amber' : 'red'}
          target={target.avgCompliance} peer={peerStats.avgCompliance}
        />
        <KPICard
          testId="kpi-meetings" icon={Calendar} label="Meetings"
          value={target.totalMeetings.toString()}
          sublabel={`${target.comMeetings}C ${target.agmMeetings}A ${target.specialMeetings}S`}
          accent="gray" target={target.totalMeetings} peer={peerStats.totalMeetings} higherIsBetter={false}
        />
      </div>

      {/* SWOT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="swot-grid">
        <SWOTQuadrant testId="swot-strengths" title="Strengths" accent="s" icon={CheckCircle2} items={swot.strengths} />
        <SWOTQuadrant testId="swot-weaknesses" title="Weaknesses" accent="w" icon={XCircle} items={swot.weaknesses} />
        <SWOTQuadrant testId="swot-opportunities" title="Opportunities" accent="o" icon={Lightbulb} items={swot.opportunities} />
        <SWOTQuadrant testId="swot-threats" title="Threats" accent="t" icon={AlertOctagon} items={swot.threats} />
      </div>

      {/* Benchmark + Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
            <h3 className="text-base font-semibold text-[#171717] font-heading">Benchmark vs Peers ({peers.length}) & Portfolio</h3>
          </div>
          <div className="space-y-4">
            <BenchmarkBar label="Revenue" target={target.totalRevenue} peer={peerStats.revenue} portfolio={portfolioStats.revenue} />
            <BenchmarkBar label="Revenue / Lot" target={target.revenuePerLot} peer={peerStats.revenuePerLot} portfolio={portfolioStats.revenuePerLot} />
            <BenchmarkBar label="Gross Margin %" target={target.margin} peer={peerStats.margin} portfolio={portfolioStats.margin} formatter={(v) => fmtPct(v, 1)} />
            <BenchmarkBar label="Gross Margin $" target={target.profit} peer={peerStats.profit} portfolio={portfolioStats.profit} />
            <BenchmarkBar label="Lots" target={target.totalOccupiableLots} peer={peerStats.occupiableLots} portfolio={portfolioStats.occupiableLots} formatter={(v) => v != null ? Math.round(v).toString() : '—'} />
            <BenchmarkBar label="Workload / Lot" target={target.workloadPerLot} peer={peerStats.workloadPerLot} portfolio={portfolioStats.workloadPerLot} higherIsBetter={false} />
            {target.avgNPS != null && peerStats.avgNPS != null && (
              <BenchmarkBar label="NPS" target={target.avgNPS} peer={peerStats.avgNPS} portfolio={portfolioStats.avgNPS} formatter={(v) => fmtNum(v, 1)} />
            )}
            <BenchmarkBar label="Meetings (annual)" target={target.totalMeetings} peer={peerStats.totalMeetings} portfolio={portfolioStats.totalMeetings} formatter={(v) => v != null ? Math.round(v).toString() : '—'} higherIsBetter={false} />
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
            <h3 className="text-base font-semibold text-[#171717] font-heading">Peer Index (100 = peer average)</h3>
          </div>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(0,0,0,0.08)" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: '#525252' }} />
                <PolarRadiusAxis angle={90} domain={[0, 200]} tick={{ fontSize: 9, fill: '#A3A3A3' }} />
                <Radar name="Peer baseline" dataKey="peer" stroke="#A3A3A3" fill="#A3A3A3" fillOpacity={0.1} strokeDasharray="3 3" />
                <Radar name="This group" dataKey="target" stroke="#171717" fill="#171717" fillOpacity={0.25} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-2 shadow-lg text-xs">
                      <p className="font-semibold text-[#171717]">{label}</p>
                      {payload.map(p => <p key={p.name} style={{ color: p.color }}>{p.name}: {Math.round(p.value)}</p>)}
                    </div>
                  );
                }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Peer Table */}
      {peers.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] rounded-[12px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="peer-table">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <h3 className="text-base font-semibold text-[#171717] font-heading">Peer Comparison Table</h3>
            <p className="text-xs text-[#737373] mt-0.5">{peers.length} peer group{peers.length > 1 ? 's' : ''} within {peerMode === 'strict' ? '±25% revenue and ±20% lots' : '±25% revenue (relaxed: lot-count band widened due to few exact matches)'}.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FA] border-b border-[#E5E5E5]">
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                  <th className="px-4 py-2.5 text-left">Group</th>
                  <th className="px-4 py-2.5 text-left">Property</th>
                  <th className="px-4 py-2.5 text-left">Manager</th>
                  <th className="px-3 py-2.5 text-right">Revenue</th>
                  <th className="px-3 py-2.5 text-right">Rev / Lot</th>
                  <th className="px-3 py-2.5 text-right">Gross Margin %</th>
                  <th className="px-3 py-2.5 text-right">Gross Margin</th>
                  <th className="px-3 py-2.5 text-right">Lots</th>
                  <th className="px-3 py-2.5 text-center">NPS</th>
                  <th className="px-3 py-2.5 text-center">Meetings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F5F5F5]">
                <tr className="bg-[#FAFAFA] font-semibold">
                  <td className="px-4 py-3 font-mono text-xs text-[#171717]">{target.basePlan}</td>
                  <td className="px-4 py-3 text-xs text-[#171717]">{target.propertyName || '—'} <span className="ml-1 text-[9px] uppercase tracking-wider text-[#737373]">(this group)</span></td>
                  <td className="px-4 py-3 text-xs text-[#525252]">{target.primaryManager}</td>
                  <td className="px-3 py-3 text-right text-xs font-mono">{fmt(target.totalRevenue)}</td>
                  <td className="px-3 py-3 text-right text-xs font-mono">{fmt(target.revenuePerLot)}</td>
                  <td className="px-3 py-3 text-right text-xs font-mono">{fmtPct(target.margin, 1)}</td>
                  <td className={`px-3 py-3 text-right text-xs font-mono ${target.profit < 0 ? 'text-red-600' : ''}`}>{fmt(target.profit)}</td>
                  <td className="px-3 py-3 text-right text-xs font-mono">{target.totalOccupiableLots || '—'}</td>
                  <td className="px-3 py-3 text-center text-xs">{target.avgNPS != null ? fmtNum(target.avgNPS) : '—'}</td>
                  <td className="px-3 py-3 text-center text-xs">{target.totalMeetings}</td>
                </tr>
                {peers.map((p) => (
                  <tr key={p.basePlan} className="hover:bg-[#F8F9FA]/50">
                    <td className="px-4 py-3 font-mono text-xs text-[#525252]">{p.basePlan}</td>
                    <td className="px-4 py-3 text-xs text-[#525252] truncate max-w-[180px]">{p.propertyName || '—'}</td>
                    <td className="px-4 py-3 text-xs text-[#525252]">{p.manager}</td>
                    <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{fmt(p.totalRevenue)}</td>
                    <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{fmt(p.revenuePerLot)}</td>
                    <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{fmtPct(p.margin, 1)}</td>
                    <td className={`px-3 py-3 text-right text-xs font-mono ${p.profit < 0 ? 'text-red-600' : 'text-[#525252]'}`}>{fmt(p.profit)}</td>
                    <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{p.totalOccupiableLots || '—'}</td>
                    <td className="px-3 py-3 text-center text-xs text-[#525252]">{p.avgNPS != null ? fmtNum(p.avgNPS) : '—'}</td>
                    <td className="px-3 py-3 text-center text-xs text-[#525252]">{p.totalMeetings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Verbatim NPS Comments */}
      {npsComments.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="nps-verbatim">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
            <h3 className="text-base font-semibold text-[#171717] font-heading">Verbatim Client Feedback</h3>
          </div>
          <div className="space-y-2">
            {npsComments.map((c, i) => {
              const accent = c.score >= 9 ? 'border-emerald-200 bg-emerald-50/30' : c.score >= 7 ? 'border-amber-200 bg-amber-50/30' : 'border-red-200 bg-red-50/30';
              return (
                <div key={`${c.planNumber}-${i}`} className={`border rounded-[10px] px-4 py-2.5 ${accent}`}>
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="text-[10px] font-mono font-semibold text-[#525252]">{c.planNumber}</span>
                    <span className="text-[10px] font-bold text-[#171717]">Score: {c.score}</span>
                  </div>
                  <p className="text-xs text-[#525252] italic leading-relaxed">"{c.comment}"</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Plans in Group */}
      {target.plans.length > 1 && (
        <div className="bg-white border border-[#E5E5E5] rounded-[12px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <h3 className="text-base font-semibold text-[#171717] font-heading">Plans in Group ({target.planCount})</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] border-b border-[#E5E5E5]">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                <th className="px-4 py-2.5 text-left">Plan</th>
                <th className="px-4 py-2.5 text-left">Manager</th>
                <th className="px-3 py-2.5 text-right">Revenue</th>
                <th className="px-3 py-2.5 text-right">Workload</th>
                <th className="px-3 py-2.5 text-right">Lots</th>
                <th className="px-3 py-2.5 text-center">Persona</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {target.plans.map(p => (
                <tr key={p.planNumber} className="hover:bg-[#F8F9FA]/50">
                  <td className="px-4 py-3 font-mono text-xs text-[#171717]">{p.planNumber}</td>
                  <td className="px-4 py-3 text-xs text-[#525252]">{p.manager}</td>
                  <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{p.revenue ? fmt(p.revenue) : '—'}</td>
                  <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{p.workloadCost ? fmt(p.workloadCost) : '—'}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#525252]">{p.occupiableLots || '—'}</td>
                  <td className="px-3 py-3 text-center text-xs text-[#525252]">{p.persona}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// Scale to peer = 100 (200 = double the peer, 50 = half the peer)
function scale(target, peer) {
  if (target == null || peer == null || peer === 0) return 0;
  return Math.min(200, Math.max(0, (target / peer) * 100));
}

export default PlanGroupDeepDive;
